/**
 * "Short", one definition (#2027 item 5, 7 Oct 2026).
 *
 * The roster page said "short" in three places that disagreed: the summary
 * strip's **Coverage** chip counted coverage-review CELLS (one ward, one
 * session, one day, below its headcount or a grade band — a cell two seats
 * short counted once, pooled floors on the side, calls not at all), while
 * the grid's status mark and Home's **Coverage · this week** tile summed the
 * roster read's `shortfalls_by_day` (empty seats). After a solve the chip
 * read `60 short · 8 floors` beside the grid's `−188 short`; after seating
 * two covers the chip read `1 short` beside *Fully covered*.
 *
 * Now every surface that says "short" reads THIS: the number of seats below
 * a minimum across the dates, from the roster read's per-day map — station
 * headcount per session, on-call daily holders, and each pooled group floor
 * once as the group (never per station). Deficits only: the server clamps
 * each seat at zero (`max(0, need − seated)`), so a ward with a spare person
 * never offsets one short elsewhere; this sum also ignores anything ≤ 0.
 *
 * Pure (no React), unit-tested in `web/tests/issue_2027_coverage_counts.test.tsx`.
 */

export interface SeatsShortDay {
  date: string;
  seats: number;
}

export interface SeatsShort {
  /** Empty seats across the dates — the one "N short" figure. */
  seats: number;
  /** Dates with at least one empty seat. */
  shortDays: number;
  /** One entry per date asked about, in the order given. */
  days: SeatsShortDay[];
}

/**
 * Seats short over `dates` from a roster read's `shortfalls_by_day`
 * (ISO date → empty seats that day; zero days omitted by the server).
 */
export function seatsShortOver(
  shortfallsByDay: Readonly<Record<string, number>> | null | undefined,
  dates: ReadonlyArray<string>,
): SeatsShort {
  const days = dates.map((date) => {
    const n = shortfallsByDay?.[date];
    return { date, seats: typeof n === "number" && n > 0 ? n : 0 };
  });
  return {
    seats: days.reduce((sum, d) => sum + d.seats, 0),
    shortDays: days.filter((d) => d.seats > 0).length,
    days,
  };
}

/** `3 seats short` / `1 seat short`. */
export function seatsShortWords(seats: number): string {
  return `${seats} ${seats === 1 ? "seat" : "seats"} short`;
}
